// Service pour la gestion des utilisateurs utilisant fetch natif
import { RootState, store } from "../store";
import { Dayjs } from "dayjs";

// URL de base de l'API
const API_BASE_URL = `${
  import.meta.env.VITE_API_URL || "http://localhost:5000/api"
}`;

// Import du middleware pour gérer les erreurs 401
import { handleUnauthorizedError } from './apiMiddleware';

// Types d'accès aux modules de l'application
export type AccessType =
  | "hr"
  | "admin"
  | "shopify"
  | "resources"
  | "reporting"
  | "approvisionnement"
  | "analyse"
  | "finance"
  | "catalog"
  | "retailers"
  | "store"
  | "store_planning"
  | "store_team"
  | "store_sales"
  | "store_regulation";

// Types
export enum ContractType {
  FREELANCE = "freelance",
  INTERIM = "intérim",
  INTERNSHIP = "stage",
  APPRENTICESHIP = "apprentissage",
  EMPLOYEE = "employé",
}

export interface Department {
  _id: string;
  key: string;
  label?: string;
  isStore?: boolean;
  isSupplier?: boolean;
  isAccountManagement?: boolean;
  sharedMailboxUserIds?: string[];
}

export interface SupplierAccess {
  _id?: string;
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  function: string;
}

// Interface pour représenter un utilisateur
export interface User {
  _id?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  password?: string; // Optionnel car non retourné par l'API et non nécessaire pour les updates
  microsoftId?: string;
  accesses: AccessType[];
  contract?: ContractType;
  hourlyRate?: number;
  monthlySalary?: number;
  supplierId?: string;
  departmentId?: string;
  shopLocationId?: string;
  function?: string;
  level?: 'employee' | 'manager1' | 'manager2';
  startDate?: Date;
  endDate?: Date;
  email?: string;
  externalId?: string; // Code vendeur externe pour liaison avec Shopify
  active: boolean;
  // Champs de périmètre pour l'accès reporting
  reportingScopeType?: 'all' | 'supplier' | 'brands';
  reportingSupplierId?: string | null;
  reportingBrands?: string[];
  supplierAccesses?: SupplierAccess[];
  departmentFlags?: {
    isStore?: boolean;
    isSupplier?: boolean;
    isAccountManagement?: boolean;
  };
  departmentKey?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
}

// Interface pour les données du formulaire
export interface UserFormData {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  password: string;
  contract?: ContractType;
  hourlyRate?: number;
  monthlySalary?: number;
  supplierId?: string;
  departmentId?: string;
  function?: string;
  shopLocationId?: string;
  level?: 'employee' | 'manager1' | 'manager2';
  startDate?: Dayjs;
  endDate?: Dayjs;
  email?: string;
  externalId?: string;
  microsoftId?: string;
  active: boolean;
  accesses: AccessType[];
  // Champs du formulaire pour le périmètre reporting
  reportingScopeType?: 'all' | 'supplier' | 'brands';
  reportingSupplierId?: string | null;
  reportingBrands?: string[];
  supplierAccesses?: SupplierAccess[];
  isReportingAccess?: boolean;
  supplierCatalogAccess?: boolean;
}

// Interface pour les données envoyées à l'API
export interface UserApiData {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  password: string;
  contract?: ContractType;
  hourlyRate?: number;
  monthlySalary?: number;
  supplierId?: string;
  departmentId?: string;
  function?: string;
  shopLocationId?: string;
  level?: 'employee' | 'manager1' | 'manager2'; // Ajout de level
  startDate?: string; // Format YYYY-MM-DD pour l'API
  endDate?: string; // Format YYYY-MM-DD pour l'API
  email?: string;
  externalId?: string;
  microsoftId?: string;
  active: boolean;
  accesses: AccessType[];
  // Champs envoyés à l'API pour le périmètre reporting
  reportingScopeType?: 'all' | 'supplier' | 'brands';
  reportingSupplierId?: string | null;
  reportingBrands?: string[];
  supplierAccesses?: SupplierAccess[];
}

// Fonction pour récupérer le token d'authentification depuis le store Redux
const getAuthToken = (): string | null => {
  const state = store.getState() as RootState;
  return state.user.currentUser?.token || null;
};

export const getDepartments = async (): Promise<Department[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/departments`, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la récupération des départements:", error);
    throw error;
  }
};

// Fonction pour générer les en-têtes avec le token d'authentification
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Obtenir tous les utilisateurs (requiert des droits admin)
 * @returns Liste de tous les utilisateurs
 */
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/users`, {
      headers: getAuthHeaders(),
    });
    
    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }
    
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la récupération des utilisateurs:", error);
    throw error;
  }
};

/**
 * Obtenir tous les utilisateurs actifs (requiert des droits admin ou HR)
 * @returns Liste des utilisateurs actifs
 */
export const getActiveUsers = async (): Promise<User[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/users/active`, {
      headers: getAuthHeaders(),
      cache: 'no-store',
    });
    
    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }
    
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la récupération des utilisateurs actifs:", error);
    throw error;
  }
};

/**
 * Créer un nouvel utilisateur (requiert des droits admin)
 * @param user Données de l'utilisateur à créer
 * @returns L'utilisateur créé
 */
export const createUser = async (user: Omit<User, "_id"> | UserApiData): Promise<User> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/users`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(user),
    });
    
    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la création de l'utilisateur:", error);
    throw error;
  }
};

/**
 * Mettre à jour un utilisateur (requiert des droits admin ou être l'utilisateur concerné)
 * @param id ID de l'utilisateur à mettre à jour
 * @param userData Données à mettre à jour
 * @returns L'utilisateur mis à jour
 */
export const updateUser = async (
  id: string,
  userData: Partial<User>
): Promise<User> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/users/${id}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    
    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la mise à jour de l'utilisateur ${id}:`, error);
    throw error;
  }
};

/**
 * Désactiver un utilisateur (requiert des droits admin ou être l'utilisateur concerné)
 * @param id ID de l'utilisateur à désactiver
 * @returns Message de confirmation
 */
export const deleteUser = async (id: string): Promise<{ message: string; user: User }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/users/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    
    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la désactivation de l'utilisateur ${id}:`, error);
    throw error;
  }
};

/**
 * Désactiver un utilisateur au lieu de le supprimer
 * @param id ID de l'utilisateur à désactiver
 * @returns Message de succès et utilisateur désactivé
 */
export const deactivateUser = async (id: string): Promise<{ message: string; user: User }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/users/${id}?deactivate=true`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    
    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la désactivation de l'utilisateur ${id}:`, error);
    throw error;
  }
};

/**
 * Obtenir les types de contrat disponibles
 * @returns Liste des types de contrat
 */
export const getContractTypes = async (): Promise<string[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/users/contract-types`, {
      headers: getAuthHeaders(),
    });
    
    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }
    
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la récupération des types de contrat:", error);
    // En cas d'erreur, retourner les valeurs par défaut du côté client
    return Object.values(ContractType);
  }
};

/**
 * Obtenir un utilisateur par son ID
 * @param id ID de l'utilisateur
 * @returns L'utilisateur trouvé
 */
export const getUserById = async (id: string): Promise<User> => {
  try {
    const response = await fetch(`${API_BASE_URL}/hr/users/${id}`, {
      headers: getAuthHeaders(),
    });
    
    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Utilisateur non trouvé");
      }
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'utilisateur ${id}:`, error);
    throw error;
  }
};
